import{useEffect,useMemo,useRef,useState}from"react";
import{authApi}from"../../lib/authApi";
import{useAppI18n}from"../../lib/appI18n";
import{QRCodeSVG}from"qrcode.react";
import{runLoginCeremony,runPrfCeremony,runRegistrationCeremony}from"../../lib/webauthn";

/**
 * Minimal production login / registration experience.
 * - Email + password uses the canonical KNSAIX auth endpoints.
 * - Social providers are shown truthfully: none are configured in this
 *   deployment yet, so they report "unavailable" instead of
 *   faking success. No privileges ever derive from an auth provider.
 */

type Mode="signin"|"register";

export function AuthScreen({onAuthenticated}:{onAuthenticated:(email:string)=>void}){

  const{t}=useAppI18n();

  const [mode,setMode]=useState<Mode>("signin");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [password2,setPassword2]=useState("");
  const [name,setName]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState<string|null>(null);
  const [info,setInfo]=useState<string|null>(null);
  const [resendBusy,setResendBusy]=useState(false);
  const [providers,setProviders]=useState<Array<{key:string;display_name:string}>>([]);
  const [screen,setScreen]=useState<"form"|"check-email"|"owner-pending"|"mfa"|"mfa-enroll"|"recovery">("form");
  const [mfaCode,setMfaCode]=useState("");
  const [mfaMethod,setMfaMethod]=useState<"totp"|"recovery">("totp");
  const [mfaFactorId,setMfaFactorId]=useState<string|null>(null);
  const [mfaSecret,setMfaSecret]=useState<string|null>(null);
  const [mfaUri,setMfaUri]=useState<string|null>(null);
  const [recoveryCodes,setRecoveryCodes]=useState<string[]>([]);
  const [legacySource,setLegacySource]=useState<string|null>(()=>{
    return new URLSearchParams(window.location.search).get("rp_handoff_target");
  });
  const [handoffTarget,setHandoffTarget]=useState<string|null>(()=>{
    return new URLSearchParams(window.location.search).get("rp_handoff_target");
  });
  const [handoffToken,setHandoffToken]=useState<string|null>(()=>{
    return new URLSearchParams(window.location.hash.slice(1)).get("rp_handoff");
  });
  // Desktop handoff state
  const [desktopHandoffPolling, setDesktopHandoffPolling] = useState(false);
  const [desktopHandoffError, setDesktopHandoffError] = useState<string|null>(null);
  const [desktopHandoffTransactionId, setDesktopHandoffTransactionId] = useState<string|null>(null);
  const [desktopHandoffBrowserSecret, setDesktopHandoffBrowserSecret] = useState<string|null>(null);
  const [desktopHandoffQrUrl, setDesktopHandoffQrUrl] = useState<string|null>(null);
  const desktopHandoffIntervalRef = useRef<number|null>(null);
  // Phone approval state (for ?desktop_approval=<token>)
  const [desktopApprovalToken, setDesktopApprovalToken] = useState<string|null>(null);
  const [desktopApprovalStep, setDesktopApprovalStep] = useState<"ready"|"approving"|"success"|"error">("ready");

  function humanize(detail:string):string{
    const map:Record<string,string>={
      invalid_credentials:"Email or password is incorrect.",
      email_already_registered:"This email is already registered. Sign in instead.",
      active_tenant_membership_required:
        "Your account is active, but it has not been granted access to a workspace yet. An administrator must authorize it.",
      passkey_authentication_required:
        "Passkey authentication is required.",
      passkey_auth_user_link_required:
        "This passkey is not linked to an active KNSAIX account.",
      auth_user_inactive:
        "This KNSAIX account is not active.",
      rp_handoff_invalid_or_expired:
        "The passkey migration expired. Start the transfer again.",
      rp_handoff_route_invalid:
        "This legacy passkey cannot be migrated to this KNSAIX domain.",
      legacy_unlock_passkey_required:
        "An unlock-capable legacy passkey is required.",
      canonical_unlock_passkey_required:
        "The new authenticator must support KNSAIX secure unlock.",
      email_verification_invalid_or_expired:
        "This verification link is invalid or expired. Request a new email.",
      public_registration_not_configured:
        "Registration is temporarily unavailable.",
      mfa_challenge_required:
        "Sign in again to continue two-factor authentication.",
      mfa_challenge_invalid_or_expired:
        "Your two-factor authentication session expired. Sign in again.",
      invalid_mfa_code:
        "The authentication code is incorrect.",
      mfa_too_many_attempts:
        "Too many incorrect authentication attempts. Sign in again.",
      mfa_service_not_configured:
        "Two-factor authentication is temporarily unavailable.",
      oauth_provider_not_available:
        "This sign-in provider is not available.",
      oauth_cancelled:
        "Provider sign-in was cancelled.",
      oauth_provider_exchange_failed:
        "The provider could not complete sign-in.",
      oauth_id_token_missing:
        "The provider identity response could not be verified.",
      oauth_id_token_invalid:
        "The provider identity response could not be verified.",
      oauth_id_token_signature_invalid:
        "The provider identity response could not be verified.",
      oauth_nonce_mismatch:
        "The provider sign-in response was rejected.",
      oauth_audience_mismatch:
        "The provider sign-in response was rejected.",
      oauth_issuer_mismatch:
        "The provider sign-in response was rejected.",
      oauth_email_required:
        "The provider did not return an email address.",
      account_link_required:
        "An account with this email already exists. Sign in to that account before linking another provider.",
      registration_rejected:
        "This registration was rejected.",
      tenant_selection_required:
        "Choose an organization before continuing.",
    };
    return map[detail]??detail.replace(/_/g," ");
  }

  useEffect(()=>{
    let cancelled=false;
    void authApi.providers().then(d=>{if(!cancelled)setProviders(d.providers||[])}).catch(()=>{if(!cancelled)setProviders([])});
    return()=>{cancelled=true};
  },[]);

  // Phone approval: consume ?desktop_approval= once and scrub it from the URL.
  // The ceremony is NOT started here; it runs only from explicit user action.
  useEffect(()=>{
    if(mode!=="signin")return;

    const params=new URLSearchParams(window.location.search);
    const token=params.get("desktop_approval");
    if(!token)return;

    window.history.replaceState(null,"",window.location.pathname);
    setDesktopApprovalToken(token);
    setDesktopApprovalStep("ready");
  },[mode]);

  async function approveDesktopSignIn(){
    if(!desktopApprovalToken||desktopApprovalStep==="approving")return;

    setDesktopApprovalStep("approving");

    try{
      const options=await authApi.desktopHandoffApprovalOptions(
        desktopApprovalToken,
      );
      const proof=await runPrfCeremony(options.ceremony_id,options.publicKey);
      await authApi.desktopHandoffApprovalVerify({
        approval_token:desktopApprovalToken,
        ceremony_id:options.ceremony_id,
        credential:proof.credential,
        prf_result:proof.prf_result,
      });
      setDesktopApprovalStep("success");
    }catch{
      // Never surface the approval token or PRF material in the UI.
      setDesktopApprovalStep("error");
    }
  }

  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const token=params.get("verify_email");
    const oauthResult=params.get("auth_result");
    const oauthError=params.get("auth_error");

    if(oauthError){
      setError(humanize(oauthError));
      window.history.replaceState(null,"",window.location.pathname);
      return;
    }

    if(oauthResult){
      window.history.replaceState(null,"",window.location.pathname);
      if(oauthResult==="check-email"){setInfo("Verification email sent. Open the link to continue.");setScreen("check-email");return}
      if(oauthResult==="check-email-mail-unavailable"){setInfo("Your provider identity was created, but verification mail could not be sent because mail delivery is not configured or failed.");setScreen("check-email");return}
      if(oauthResult==="owner-pending"){setInfo("Your email is verified. Your account is waiting for Owner approval.");setScreen("owner-pending");return}
      if(oauthResult==="mfa"){setMfaCode("");setMfaMethod("totp");setError(null);setScreen("mfa");return}
      if(oauthResult==="mfa-enroll"){setMfaCode("");void startMfaEnrollment().catch(err=>setError(err instanceof Error?humanize(err.message):"Authenticator setup failed."));return}
    }

    if(!token)return;
    setBusy(true);setError(null);
    void authApi.verifyEmail(token).then(result=>{window.history.replaceState(null,"",window.location.pathname);setInfo(result.message);setScreen("owner-pending")}).catch(err=>setError(err instanceof Error?humanize(err.message):"Email verification failed.")).finally(()=>setBusy(false));
  },[]);

  useEffect(()=>{
    if(
      mode!=="signin"
      ||handoffTarget
      ||handoffToken
    )return;

    let cancelled=false;

    void fetch(
      "/api/v1/device-authority/rp-handoff/discovery",
      {
        method:"GET",
        credentials:"include",
        headers:{Accept:"application/json"},
      },
    )
      .then(async r=>{
        if(!r.ok)return null;
        return await r.json() as {
          available?:boolean;
          source_origin?:string|null;
        };
      })
      .then(d=>{
        if(
          !cancelled
          &&d?.available
          &&typeof d.source_origin==="string"
        ){
          setLegacySource(d.source_origin);
        }
      })
      .catch(()=>{});

    return()=>{cancelled=true;};
  },[mode,handoffTarget,handoffToken]);


  async function jsonRequest<T>(
    path:string,
    init:RequestInit,
  ):Promise<T>{
    const response=await fetch(path,{
      credentials:"include",
      ...init,
      headers:{
        Accept:"application/json",
        "Content-Type":"application/json",
        ...(init.headers||{}),
      },
    });

    if(!response.ok){
      let detail=`request_failed_${response.status}`;

      try{
        const body=await response.json();
        if(typeof body?.detail==="string")detail=body.detail;
      }catch{
        /* preserve generic status */
      }

      throw new Error(detail);
    }

    return await response.json() as T;
  }


  function beginLegacyHandoff(){
    if(!legacySource)return;

    const url=new URL("/security",legacySource);

    url.searchParams.set(
      "rp_handoff_target",
      window.location.origin,
    );

    window.location.assign(url.toString());
  }


  async function authorizeLegacyHandoff(){
    if(!handoffTarget||busy)return;

    setBusy(true);
    setError(null);
    setInfo(null);

    try{
      const options=await jsonRequest<{
        ceremony_id:string;
        publicKey:unknown;
      }>(
        "/api/v1/device-authority/rp-handoff/source/options",
        {
          method:"POST",
          body:JSON.stringify({
            target_origin:handoffTarget,
          }),
        },
      );

      const proof=await runPrfCeremony(
        options.ceremony_id,
        options.publicKey,
      );

      const result=await jsonRequest<{
        verified:boolean;
        handoff_token:string;
        target_origin:string;
        expires_in_seconds:number;
      }>(
        "/api/v1/device-authority/rp-handoff/source/verify",
        {
          method:"POST",
          body:JSON.stringify({
            ...proof,
            target_origin:handoffTarget,
          }),
        },
      );

      const target=new URL(
        "/security",
        result.target_origin,
      );

      target.hash=new URLSearchParams({
        rp_handoff:result.handoff_token,
      }).toString();

      // replace prevents keeping the source migration URL in history.
      window.location.replace(target.toString());

    }catch(e){
      setError(
        e instanceof Error
          ?humanize(e.message)
          :"Legacy passkey authorization failed."
      );
    }finally{
      setBusy(false);
    }
  }


  async function completeLegacyHandoff(){
    if(!handoffToken||busy)return;

    setBusy(true);
    setError(null);
    setInfo(null);

    try{
      const options=await jsonRequest<{
        ceremony_id:string;
        publicKey:unknown;
      }>(
        "/api/v1/device-authority/rp-handoff/target/options",
        {
          method:"POST",
          body:JSON.stringify({
            handoff_token:handoffToken,
          }),
        },
      );

      const proof=await runRegistrationCeremony(
        options,
      );

      await jsonRequest(
        "/api/v1/device-authority/rp-handoff/target/verify",
        {
          method:"POST",
          body:JSON.stringify({
            handoff_token:handoffToken,
            ...proof,
            label:"iPhone — knsaix.com",
            trust_group:"canonical-migration",
          }),
        },
      );

      // Raw transfer token must disappear from browser-visible URL/state
      // immediately after successful single-use completion.
      window.history.replaceState(
        null,
        "",
        window.location.pathname,
      );

      setHandoffToken(null);
      setLegacySource(null);

      setInfo(
        "Canonical knsaix.com passkey created. Continue with passkey to sign in."
      );

    }catch(e){
      setError(
        e instanceof Error
          ?humanize(e.message)
          :"Canonical passkey creation failed."
      );
    }finally{
      setBusy(false);
    }
  }


  async function passkeySignIn(){
    if(busy)return;

    setBusy(true);
    setError(null);
    setInfo(null);

    try{
      const optionsResponse=await fetch(
        "/api/v1/device-authority/login/options",
        {
          method:"POST",
          credentials:"include",
          headers:{
            Accept:"application/json",
            "Content-Type":"application/json",
          },
          body:"{}",
        },
      );

      if(!optionsResponse.ok){
        let detail=`request_failed_${optionsResponse.status}`;
        try{
          const body=await optionsResponse.json();
          if(typeof body?.detail==="string")detail=body.detail;
        }catch{
          /* preserve generic error */
        }
        throw new Error(detail);
      }

      const options=await optionsResponse.json() as {
        ceremony_id:string;
        publicKey:unknown;
      };

      const proof=await runLoginCeremony(options);

      const verifyResponse=await fetch(
        "/api/v1/device-authority/login/verify",
        {
          method:"POST",
          credentials:"include",
          headers:{
            Accept:"application/json",
            "Content-Type":"application/json",
          },
          body:JSON.stringify(proof),
        },
      );

      if(!verifyResponse.ok){
        let detail=`request_failed_${verifyResponse.status}`;
        try{
          const body=await verifyResponse.json();
          if(typeof body?.detail==="string")detail=body.detail;
        }catch{
          /* preserve generic error */
        }
        throw new Error(detail);
      }

      // login/verify has now issued the canonical Device Authority cookie.
      // Promote that server-validated identity into the normal KNSAIX
      // primary session. No identity or tenant data comes from the client.
      await authApi.passkeySession();

      onAuthenticated("passkey");
    }catch(e){
      const detail=
        e instanceof Error
          ?e.message
          :"Passkey sign-in failed.";

      setError(humanize(detail));
    }finally{
      setBusy(false);
    }
  }

  async function startMfaEnrollment(){const e=await authApi.mfaEnrollStart();setMfaFactorId(e.factor_id);setMfaSecret(e.secret);setMfaUri(e.otpauth_uri);setScreen("mfa-enroll")}
  async function submitMfa(){if(busy||!mfaCode.trim())return;setBusy(true);setError(null);try{await authApi.mfaVerify(mfaCode.trim());onAuthenticated(email.trim()||"mfa")}catch(e){setError(e instanceof Error?humanize(e.message):"Two-factor authentication failed.")}finally{setBusy(false)}}
  async function cancelMfa(){
    if(busy)return;
    setBusy(true);
    setError(null);
    try{
      await authApi.mfaCancel();
      setMfaCode("");
      setMfaMethod("totp");
      setMfaFactorId("");
      setMfaSecret("");
      setMfaUri("");
      setRecoveryCodes([]);
      setMode("signin");
      setScreen("form");
    }catch(e){
      setError(e instanceof Error?humanize(e.message):"Could not cancel two-factor authentication.");
    }finally{
      setBusy(false);
    }
  }
  
  async function confirmMfaEnrollment(){if(busy||!mfaFactorId||!mfaCode.trim())return;setBusy(true);setError(null);try{const r=await authApi.mfaEnrollConfirm(mfaFactorId,mfaCode.trim());setRecoveryCodes(r.recovery_codes);setScreen("recovery")}catch(e){setError(e instanceof Error?humanize(e.message):"Authenticator setup failed.")}finally{setBusy(false)}}
  async function resendVerification(){
    if(resendBusy||!email.trim())return;
    setResendBusy(true);
    setError(null);
    try{
      const result=await authApi.resendVerification(email.trim());
      setInfo(result.message||"If verification is still required, a new email will be sent.");
    }catch(e){
      setError(e instanceof Error?humanize(e.message):"Verification email could not be sent.");
    }finally{
      setResendBusy(false);
    }
  }

  async function submit(){if(busy)return;setBusy(true);setError(null);setInfo(null);try{if(mode==="signin"){const result=await authApi.login(email.trim(),password);if(result.mfa_required){setMfaCode("");setMfaMethod("totp");if(result.mfa_enrollment_required)await startMfaEnrollment();else setScreen("mfa");return}onAuthenticated(email.trim())}else{const result=await authApi.register({display_name:name.trim(),email:email.trim(),password,password_confirm:password2});setPassword("");setPassword2("");setInfo(result.verification_delivery==="SENT"?"Verification email sent. Open the link to continue.":result.verification_delivery==="FAILED"?"Your account was created, but the verification email could not be sent. Try resend after mail delivery is restored.":"Your account is pending email verification. Mail delivery is not configured on this deployment yet.");setScreen("check-email")}}catch(e){setError(e instanceof Error?humanize(e.message):"Something went wrong.")}finally{setBusy(false)}}

  async function startDesktopHandoff() {
    if (busy) return;
    // The approval screen must never chain into a new QR handoff.
    if (desktopApprovalToken) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const result = await authApi.desktopHandoffStart();
      setDesktopHandoffTransactionId(result.transaction_id);
      setDesktopHandoffBrowserSecret(result.browser_secret);
      
      // Generate QR code for the approval URL and store in state
      setDesktopHandoffQrUrl(window.location.origin + result.approval_path);
      
      // Start polling for completion
      setDesktopHandoffPolling(true);
      desktopHandoffIntervalRef.current = window.setInterval(async () => {
        try {
          const pollResult = await authApi.desktopHandoffComplete({
            transaction_id: result.transaction_id,
            browser_secret: result.browser_secret
          });
          
          if (pollResult.status === "PENDING") {
            // Still polling, continue
            return;
          }
          
          if (pollResult.authenticated) {
            // Success! Stop polling and clear interval
            setDesktopHandoffPolling(false);
            if (desktopHandoffIntervalRef.current) {
              window.clearInterval(desktopHandoffIntervalRef.current);
              desktopHandoffIntervalRef.current = null;
            }
            
            // Clear the polling state
            setDesktopHandoffTransactionId(null);
            setDesktopHandoffBrowserSecret(null);
            setDesktopHandoffQrUrl(null);
            
            // Navigate to intended destination or home
            window.location.href = "/";
          } else {
            // Some other status that requires stopping
            setDesktopHandoffPolling(false);
            if (desktopHandoffIntervalRef.current) {
              window.clearInterval(desktopHandoffIntervalRef.current);
              desktopHandoffIntervalRef.current = null;
            }
            setDesktopHandoffError("Handoff failed or expired");
          }
        } catch (err) {
          setDesktopHandoffPolling(false);
          if (desktopHandoffIntervalRef.current) {
            window.clearInterval(desktopHandoffIntervalRef.current);
            desktopHandoffIntervalRef.current = null;
          }
          setDesktopHandoffError(err instanceof Error ? humanize(err.message) : "Handoff failed");
        }
      }, 1500); // Poll every 1.5 seconds
      
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? humanize(err.message) : "Failed to start handoff");
    } finally {
      setBusy(false);
    }
  }

  const input=(value:string,set:(v:string)=>void,type="text",label:string,auto?:string)=>(
    <label className="auth-field">
      <span>{label}</span>
      <input
        type={type} value={value} autoComplete={auto}
        onChange={e=>set(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter")void submit();}}
        style={{minHeight:"44px"}}
      />
    </label>
  );


  const providerIcon=(key:string)=>key==="GOOGLE"?<svg className="auth-provider-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12.2c0-.7-.1-1.3-.2-1.9H12v3.6h5.1c-.2 1.2-.9 2.2-1.9 2.9v2.4h3.1c1.8-1.7 2.7-4.1 2.7-7z"/><path d="M12 21c2.6 0 4.8-.9 6.4-2.3l-3.1-2.4c-.9.6-2 .9-3.3.9-2.5 0-4.6-1.7-5.4-4H3.4v2.5A9.7 9.7 0 0 0 12 21z"/><path d="M6.6 13.2A5.8 5.8 0 0 1 6.3 12c0-.4.1-.8.2-1.2V8.3H3.4A9 9 0 0 0 2.5 12c0 1.3.3 2.6.9 3.7l3.2-2.5z"/><path d="M12 6.8c1.4 0 2.7.5 3.7 1.4l2.8-2.8A9.3 9.3 0 0 0 12 3a9.7 9.7 0 0 0-8.6 5.3l3.2 2.5c.8-2.3 2.9-4 5.4-4z"/></svg>:key==="MICROSOFT"?<svg className="auth-provider-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z"/></svg>:key==="APPLE"?<svg className="auth-provider-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M16.7 13.2c0-2.5 2.1-3.7 2.2-3.8a4.8 4.8 0 0 0-3.8-2.1c-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.3-.9A4.9 4.9 0 0 0 3.8 9.8c-1.7 3-.4 7.5 1.2 9.9.8 1.2 1.8 2.5 3.1 2.4 1.2-.1 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.1-.8-2.1-3.7zM14 5.6c.7-.9 1.2-2.1 1.1-3.3-1.1 0-2.4.7-3.2 1.6-.7.8-1.3 2-1.1 3.2 1.2.1 2.4-.6 3.2-1.5z"/></svg>:null;
  const title=useMemo(()=>screen==="check-email"?"Check your email":screen==="owner-pending"?"Registration verified":screen==="mfa"?"Two-factor authentication":screen==="mfa-enroll"?"Secure your account":screen==="recovery"?"Save your recovery codes":mode==="signin"?"Welcome back":"Create account",[screen,mode]);
  if(screen==="recovery")return <div className="auth-screen"><main className="auth-card auth-card-wide" aria-live="polite"><div className="auth-brand">KNSAIX</div><h1>{title}</h1><p className="auth-copy"><strong>These codes are shown only once.</strong> Save them before continuing. Each code can be used one time if you lose access to your authenticator.</p><div className="auth-recovery-grid">{recoveryCodes.map(code=><code key={code}>{code}</code>)}</div><div style={{display:"flex",gap:"10px",flexWrap:"wrap",marginTop:"16px"}}><button className="auth-secondary" type="button" onClick={()=>void navigator.clipboard.writeText(recoveryCodes.join("\n"))}>Copy all codes</button><button className="auth-secondary" type="button" onClick={()=>{const blob=new Blob([recoveryCodes.join("\n")+"\n"],{type:"text/plain"});const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download="knsaix-recovery-codes.txt";a.click();URL.revokeObjectURL(url)}}>Save as file</button></div><button className="auth-submit" type="button" onClick={()=>onAuthenticated(email.trim()||"mfa")}>I saved the recovery codes</button></main></div>;
  if(screen==="mfa"||screen==="mfa-enroll")return <div className="auth-screen"><main className="auth-card" aria-busy={busy} aria-live="polite">
  <div className="auth-brand">KNSAIX</div>
  <h1>{title}</h1>

  {screen==="mfa-enroll"&&<>
    <p className="auth-copy">Scan the QR code with Google Authenticator, Microsoft Authenticator, 1Password or another TOTP app. Then enter the current 6-digit code.</p>
    {mfaUri&&<div style={{background:"#fff",padding:"14px",borderRadius:"12px",width:"fit-content",margin:"16px auto"}}><QRCodeSVG value={mfaUri} size={190} level="M" marginSize={1}/></div>}
    {mfaSecret&&<details className="auth-details"><summary>Can’t scan? Use setup key</summary><div className="auth-secret-box"><span>Setup key</span><code>{mfaSecret}</code></div></details>}
    {mfaUri&&<details className="auth-details"><summary>Authenticator URI</summary><code>{mfaUri}</code></details>}
  </>}

  {screen==="mfa"&&mfaMethod==="totp"&&
    <p className="auth-copy">Enter the current 6-digit code from your authenticator app.</p>
  }

  {screen==="mfa"&&mfaMethod==="recovery"&&
    <p className="auth-copy">Enter one of your unused recovery codes.</p>
  }

  <label className="auth-field">
    <span>{screen==="mfa"&&mfaMethod==="recovery"?"Recovery code":"Authenticator code"}</span>
    <input
      value={mfaCode}
      autoComplete={mfaMethod==="recovery"?"off":"one-time-code"}
      inputMode={screen==="mfa"&&mfaMethod==="recovery"?"text":"numeric"}
      maxLength={screen==="mfa"&&mfaMethod==="recovery"?32:6}
      onChange={e=>{
        const value=e.target.value;
        setMfaCode(
          screen==="mfa"&&mfaMethod==="recovery"
            ? value.toUpperCase()
            : value.replace(/\D/g,"").slice(0,6)
        );
      }}
      onKeyDown={e=>{
        if(e.key==="Enter")void(screen==="mfa"?submitMfa():confirmMfaEnrollment());
      }}
    />
  </label>

  {error&&<p className="auth-error">{error}</p>}

  <button
    className="auth-submit"
    type="button"
    disabled={busy||!mfaCode.trim()}
    onClick={()=>void(screen==="mfa"?submitMfa():confirmMfaEnrollment())}
  >
    {busy?"Verifying…":screen==="mfa"?(mfaMethod==="recovery"?"Verify recovery code":"Verify"):"Finish setup"}
  </button>

  {screen==="mfa"&&
    <button
      className="auth-secondary"
      type="button"
      disabled={busy}
      onClick={()=>{
        setMfaCode("");
        setError(null);
        setMfaMethod(method=>method==="totp"?"recovery":"totp");
      }}
    >
      {mfaMethod==="totp"?"Use a recovery code":"Use authenticator code"}
    </button>
  }

  <button
    className="auth-link"
    type="button"
    disabled={busy}
    onClick={()=>void cancelMfa()}
  >
    Back to sign in
  </button>
</main></div>;
  if(screen==="check-email"||screen==="owner-pending")return <div className="auth-screen"><main className="auth-card" aria-live="polite"><div className="auth-brand">KNSAIX</div><h1>{title}</h1><p className="auth-copy">{screen==="check-email"?"Verify your email address before the Owner can activate your access.":"Your email is verified. The Owner can now assign your KNSAIX login profile and access permissions."}</p>{info&&<p className="auth-info">{info}</p>}{error&&<p className="auth-error">{error}</p>}{screen==="check-email"&&email&&<button type="button" className="auth-secondary" disabled={resendBusy} onClick={()=>void resendVerification()}>{resendBusy?"Sending…":"Resend verification email"}</button>}<button type="button" className="auth-link" onClick={()=>{setScreen("form");setMode("signin");setError(null)}}>Back to sign in</button></main></div>;
  if(desktopApprovalToken)return <div className="auth-screen"><main className="auth-card" aria-live="polite"><div className="auth-brand">KNSAIX</div><h1>{t("dh.approvalTitle")}</h1>
  <p className="auth-copy">{t("dh.approvalBody")}</p>
  {desktopApprovalStep==="success"&&<p className="auth-info">{t("dh.approved")}</p>}
  {desktopApprovalStep==="error"&&<p className="auth-error">{t("dh.failed")}</p>}
  {desktopApprovalStep==="approving"&&<p className="auth-info">{t("dh.approving")}</p>}
  {desktopApprovalStep==="ready"&&<button className="auth-submit" type="button" onClick={()=>void approveDesktopSignIn()}>{t("dh.approve")}</button>}
  </main></div>;
  return <div className="auth-screen"><main className="auth-card" aria-busy={busy}><div className="auth-brand">KNSAIX</div><h1>{title}</h1><p className="auth-tagline">{mode==="signin"?"Sign in securely to continue":"Create your identity. Access is assigned separately by the Owner."}</p>
  {mode==="signin"&&handoffTarget&&<button type="button" className="auth-primary-provider" disabled={busy} onClick={()=>void authorizeLegacyHandoff()}>{busy?"Waiting for legacy passkey…":"Authorize existing passkey transfer"}</button>}
  {mode==="signin"&&handoffToken&&<button type="button" className="auth-primary-provider" disabled={busy} onClick={()=>void completeLegacyHandoff()}>{busy?"Creating canonical passkey…":"Create knsaix.com passkey"}</button>}
  {mode==="signin"&&!handoffTarget&&!handoffToken&&!desktopApprovalToken&&<button type="button" className="auth-primary-provider" disabled={busy} onClick={()=>void passkeySignIn()}><svg className="auth-passkey-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="8.5" cy="8.5" r="3.5"/><path d="M12 11h9v3h-2v2h-3v2h-3v-4h-1z"/></svg><span>{busy?t("auth.waitingPasskey"):t("auth.signInWithPasskey")}</span></button>}
  {mode==="signin"&&!handoffTarget&&!handoffToken&&!desktopApprovalToken&&<button type="button" className="auth-secondary" disabled={busy} onClick={()=>void startDesktopHandoff()}>{busy?t("dh.waitingIphone"):t("auth.approveWithAnotherIphone")}</button>}
  {desktopHandoffPolling && desktopHandoffQrUrl && (
    <div className="auth-field">
      <div className="auth-copy">{t("dh.scanQr")}</div>
      <div style={{display:"flex",justifyContent:"center",margin:"16px 0"}}>
        <QRCodeSVG value={desktopHandoffQrUrl} size={180} level="M" marginSize={1} />
      </div>
    </div>
  )}
  {desktopHandoffPolling && (
    <div className="auth-field">
      <div className="auth-copy">{t("dh.waitingApproval")}</div>
      <div className="auth-copy">{t("dh.completeOnIphone")}</div>
    </div>
  )}
  {desktopHandoffError && (
    <p className="auth-error">{desktopHandoffError}</p>
  )}
  {providers.length>0&&<div className="auth-providers">{providers.map(provider=><button key={provider.key} type="button" className="auth-provider" onClick={()=>window.location.assign(`/api/v1/auth/oauth/${provider.key.toLowerCase()}/start`)}>{providerIcon(provider.key)}<span>Continue with {provider.display_name}</span></button>)}</div>}
  <div className="auth-divider"><span>or</span></div>{mode==="register"&&input(name,setName,"text","Name","name")}{input(email,setEmail,"email","Email","email")}{input(password,setPassword,"password","Password",mode==="register"?"new-password":"current-password")}{mode==="register"&&input(password2,setPassword2,"password","Confirm password","new-password")}
  <div className="auth-live" aria-live="polite">{error&&<p className="auth-error">{error}</p>}{info&&<p className="auth-info">{info}</p>}</div><button className="auth-submit" type="button" disabled={busy||!email||!password||(mode==="register"&&(!name||!password2))} onClick={()=>void submit()}>{busy?"Working…":mode==="signin"?"Sign in":"Create account"}</button><button type="button" className="auth-switch" onClick={()=>{setMode(m=>m==="signin"?"register":"signin");setError(null);setInfo(null)}}>{mode==="signin"?"New to KNSAIX? Create account":"Already have an account? Sign in"}</button>
  {mode==="signin"&&!handoffTarget&&!handoffToken&&legacySource&&<div className="auth-help"><span>Having trouble signing in?</span><button type="button" className="auth-link" disabled={busy} onClick={beginLegacyHandoff}>Use an existing legacy passkey</button></div>}</main></div>;
}
